// @ts-nocheck

import { createSelector } from 'reselect';

import { __ } from '../../i18n/l10n';
import { selectBoomUserNotifications } from '../../modules/boom/selectors/user/userPrimary.selector';
import { getGRLabelsBySkinId } from '../../modules/gift-race/constants/labels';
import { selectGRGlobalSkinId } from '../../modules/gift-race/selectors/global/globalPrimary.selector';
import { selectGRUserNotifications } from '../../modules/gift-race/selectors/user/userPrimary.selector';
import { selectKLUserNotifications } from '../../modules/koh-lanta/selectors/user';
import { selectActiveMissions } from '../../modules/missions/selectors/missions';
import { selectMPUserNotifications } from '../../modules/monopoly/selectors/user';
import {
  getPathnameFromProps,
  getSeenMissionsFromProps,
  getTabBarItemIdFromProps,
} from '../../selectors/_propsGetters_';
import { type SelectorMaker } from '../../types/selectors';
import { type TabBarNavigationType } from '../../types/tabBar';
import { TabBarNavigationTypes } from '../../types/tabBar';
import Routes from '../../utils/routes';

const emptyStr = '';
const emptyFnReturningFalse = () => false;

const LabelsByTabBarNavigationType = new Proxy(
  {
    [TabBarNavigationTypes.HOME]: __('Home'),
    [TabBarNavigationTypes.SPORTS]: __('Sports'),
    [TabBarNavigationTypes.LIVE]: __('Live'),
    [TabBarNavigationTypes.MY_BETS]: __('My bets'),
    [TabBarNavigationTypes.BASKET]: __('Betslip'),
    [TabBarNavigationTypes.EXTRAS]: __('Extras'),
    [TabBarNavigationTypes.KOH_LANTA]: __('Koh-Lanta'),
    [TabBarNavigationTypes.GRIDS]: __('Grids'),
    [TabBarNavigationTypes.GRIDS_HISTORY]: __('My grids'),
    [TabBarNavigationTypes.MONOPOLY]: 'Monopoly',
    [TabBarNavigationTypes.BOOM]: 'Boom',
  },
  {
    get(origin, key) {
      if (!origin[key]) {
        return emptyStr;
      }
      return origin[key];
    },
  },
);

const GetIsHighlightByTabBarNavigationType = new Proxy(
  {
    [TabBarNavigationTypes.HOME]: (pathname) => Routes.isHome(pathname),
    [TabBarNavigationTypes.SPORTS]: (pathname) => Routes.isSports(pathname),
    [TabBarNavigationTypes.LIVE]: (pathname) => Routes.isLive(pathname),
    [TabBarNavigationTypes.MY_BETS]: (pathname) => Routes.isHistory(pathname),
    [TabBarNavigationTypes.GRIDS]: (pathname) => Routes.isGrids(pathname),
    [TabBarNavigationTypes.GRIDS_HISTORY]: (pathname) => Routes.isGridsHistory(pathname),
    [TabBarNavigationTypes.KOH_LANTA]: (pathname) => Routes.isKL(pathname, { exact: false }),
    [TabBarNavigationTypes.GIFT_RACE]: (pathname) => Routes.isGR(pathname, { exact: false }),
    [TabBarNavigationTypes.EXTRAS]: (pathname) => Routes.isExtras(pathname, { exact: false }),
    [TabBarNavigationTypes.MONOPOLY]: (pathname) => Routes.isMonopoly(pathname, { exact: false }),
    [TabBarNavigationTypes.BOOM]: (pathname) => Routes.isBoom(pathname, { exact: false }),
  },
  {
    get(origin, key) {
      if (!origin[key]) {
        return emptyFnReturningFalse;
      }
      return origin[key];
    },
  },
);

type NavigationTabBarItemPropsType = {
  id: TabBarNavigationType;
  label: string;
  counter?: number;
  isHighlight: boolean;
  disableAnimation?: boolean;
};

const makeNavigationTabBarItemPropsSelector: SelectorMaker<NavigationTabBarItemPropsType> = () => {
  const selectHasKLPendingNotifications = createSelector(
    selectKLUserNotifications,
    (notifications) => !!notifications?.length,
  );

  const selectHasGRPendingNotifications = createSelector(
    selectGRUserNotifications,
    (notifications) => !!notifications.length,
  );

  const selectHasMPPendingNotifications = createSelector(
    selectMPUserNotifications,
    (notifications) => !!notifications.length,
  );

  const selectHasExtrasPendingNotifications = createSelector(
    [selectActiveMissions, getSeenMissionsFromProps],
    (activeMissions, seenMissions) =>
      activeMissions.some((activeMission) => seenMissions.indexOf(activeMission) === -1),
  );

  const selectHasBoomPendingNotifications = createSelector(
    selectBoomUserNotifications,
    (boomNotifications) => !!boomNotifications.length,
  );

  const selectTabBarItemLabel = createSelector(getTabBarItemIdFromProps, selectGRGlobalSkinId, (id, skinId) => {
    if (id === TabBarNavigationTypes.GIFT_RACE) {
      return getGRLabelsBySkinId(skinId)?.TabBarLabel;
    }

    return LabelsByTabBarNavigationType[id];
  });

  const selectTabBarItemLiveCounter = createSelector(
    [getTabBarItemIdFromProps, ({ entities }) => entities?.reallyLiveMatchCount],
    (id, liveCounter) => {
      if (id === TabBarNavigationTypes.LIVE) {
        return liveCounter;
      }
    },
  );

  const selectTabBarItemDisableAnimation = createSelector(
    [
      getTabBarItemIdFromProps,
      selectHasKLPendingNotifications,
      selectHasGRPendingNotifications,
      selectHasMPPendingNotifications,
      selectHasBoomPendingNotifications,
      selectHasExtrasPendingNotifications,
    ],
    (
      id,
      hasPendingKLNotifications,
      hasPendingGRNotifications,
      hasPendingMPNotifications,
      hasBoomPendingNotifications,
      hasExtrasPendingNotifications,
    ) => {
      if (id === TabBarNavigationTypes.KOH_LANTA) {
        return !hasPendingKLNotifications;
      }

      if (id === TabBarNavigationTypes.GIFT_RACE) {
        return !hasPendingGRNotifications;
      }

      if (id === TabBarNavigationTypes.MONOPOLY) {
        return !hasPendingMPNotifications;
      }

      if (id === TabBarNavigationTypes.BOOM) {
        return !hasBoomPendingNotifications;
      }

      if (id === TabBarNavigationTypes.EXTRAS) {
        return !hasExtrasPendingNotifications;
      }
    },
  );

  const selectTabBarItemIsHighlight = createSelector([getTabBarItemIdFromProps, getPathnameFromProps], (id, pathname) =>
    GetIsHighlightByTabBarNavigationType[id](pathname),
  );

  return createSelector(
    [
      getTabBarItemIdFromProps,
      selectTabBarItemLabel,
      selectTabBarItemLiveCounter,
      selectTabBarItemIsHighlight,
      selectTabBarItemDisableAnimation,
    ],
    (id, label, counter, isHighlight, disableAnimation) => ({
      id,
      label,
      counter,
      isHighlight,
      disableAnimation,
    }),
  );
};

export default makeNavigationTabBarItemPropsSelector;
